import { BaseRoleplayer, ClientValidationStatus } from '../../../common/types/client';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { GridActionsCellItemProps } from '@mui/x-data-grid';
import { JSX, ReactElement, useEffect, useState } from 'react';
import { Option } from '../../../common/types/option';
import { useQuery } from '@tanstack/react-query';
import ClientList, { ClientListProps } from '../../../clients/clientList';
import ClientStatusBadge from '../../../clients/components/clientStatusBadge';
import clsx from 'clsx';

interface SelectedParty {
  partyRoleplayerID: number;
  relationshipID: number;
}

interface SelectPartyRelationProps {
  /**
   * Current selected relationship id
   */
  currentValue: number | undefined;
  /**
   * List of available relationships
   */
  relationships: Option[];
  /**
   * Callback for selecting relationship
   */
  onSelect: (relationshipId: number) => void;
}

/**
 * Action button for selecting party and select relationship
 */
const SelectPartyRelation = ({
  currentValue,
  relationships,
  onSelect,
}: SelectPartyRelationProps): JSX.Element => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleClose = (): void => setAnchorEl(null);
  const handleSelect = (relationshipId: number): void => {
    onSelect(relationshipId);
    handleClose();
  };

  const selectedRelationship =
    currentValue === undefined ? undefined : relationships.find((item) => item.id === currentValue);

  const open = Boolean(anchorEl);

  return (
    <>
      <Button
        className={clsx((selectedRelationship || open) && 'active', 'h-10')}
        variant={selectedRelationship ? 'contained' : 'outlined'}
        onClick={({ currentTarget }): void => setAnchorEl(currentTarget)}
      >
        {selectedRelationship ? (
          <Typography lineHeight={1} variant="caption">
            {selectedRelationship.note}
          </Typography>
        ) : (
          'select'
        )}
      </Button>
      <Menu open={open} anchorEl={anchorEl} onClose={handleClose}>
        {relationships.map((item) => (
          <MenuItem key={item.id} onClick={(): void => handleSelect(item.id)}>
            {item.note || item.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

interface SelectPartiesDialogProps extends ClientListProps {
  /**
   * Flag indicating if the dialog is open
   */
  open: boolean;
  /**
   * Callback for closing the dialog (with selected parties if any)
   */
  onClose: (selectedParties?: SelectedParty[]) => void;
  /**
   * List of roleplayer ids that should not be selectable
   */
  forbiddenRoleplayerIDs?: number[];
}

/**
 * Dialog for selecting FNA parties
 */
const SelectPartiesDialog = ({
  open,
  onClose,
  forbiddenRoleplayerIDs,
  ...clientListProps
}: SelectPartiesDialogProps): JSX.Element => {
  const [selectedParties, setSelectedParties] = useState<SelectedParty[]>([]);
  const { data: relationships } = useQuery<Option[]>({
    queryKey: [`/Relationships`],
  });

  // If the dialog is closed, clear the selection
  useEffect(() => {
    if (!open) {
      setSelectedParties([]);
    }
  }, [open]);

  const getSelectedRelationId = (roleplayerId: number): number | undefined => {
    return selectedParties.find((item) => item.partyRoleplayerID === roleplayerId)?.relationshipID;
  };

  const handleClose = (): void => {
    onClose(selectedParties.length > 0 ? selectedParties : undefined);
  };

  const handleSelect = (selectedParty: SelectedParty): void => {
    setSelectedParties((value) => [...value, selectedParty]);
  };

  return (
    <Dialog maxWidth="xl" fullWidth onClose={handleClose} open={open}>
      <Box className="flex justify-between items-start">
        <DialogTitle>Choose parties</DialogTitle>
        <Button variant="contained" onClick={handleClose}>
          Save
        </Button>
      </Box>
      <DialogContent>
        <ClientList
          {...clientListProps}
          getActions={({ row }): ReactElement<GridActionsCellItemProps>[] => [
            <Box key="select" className="active">
              {forbiddenRoleplayerIDs?.includes(row.id) ? (
                <Typography variant="caption">Already added</Typography>
              ) : (row as unknown as BaseRoleplayer).validationStatusID ===
                ClientValidationStatus.valid ? (
                <SelectPartyRelation
                  relationships={relationships ?? []}
                  currentValue={getSelectedRelationId(row.id)}
                  onSelect={(relationshipID): void =>
                    handleSelect({
                      relationshipID,
                      partyRoleplayerID: row.id,
                    })
                  }
                />
              ) : (
                <ClientStatusBadge client={row as unknown as BaseRoleplayer} />
              )}
            </Box>,
          ]}
        />
      </DialogContent>
    </Dialog>
  );
};

export default SelectPartiesDialog;
